import { useCallback } from 'react'
import { applicationsFiltersStore } from '../../../model/applicationsFiltersStore'
import { applicationsStore } from '../../../model/applicationsStore'
import { essaySummaryStore } from '../../../model/essaySummaryStore'
import {
	buildCountryFilterOptions,
	buildEssayStatusFilterOptions,
	buildNeedsAttentionFilterOptions,
	sanitizePanelFilters,
} from '../../../utils/applicationsPageFilters'

export const useFiltersApplicationsContent = ({ onClose } = {}) => {
	const st = applicationsFiltersStore
	const list = applicationsStore.applications
	const rows = Array.isArray(list) ? list : []
	const essayAggregateMap = essaySummaryStore.aggregateByTargetId
	const essaysByTargetId = essaySummaryStore.essaysByTargetId

	const countryOptions = buildCountryFilterOptions(rows)
	const needsAttentionOptions = buildNeedsAttentionFilterOptions(rows, essayAggregateMap)
	const essayStatusOptions = buildEssayStatusFilterOptions(
		rows,
		essayAggregateMap,
		essaysByTargetId,
	)

	const filtersConfig = [
		{
			key: 'country',
			title: 'Country',
			options: countryOptions,
			selected: st.filters.country,
		},
		{
			key: 'needsAttention',
			title: 'Needs Attention',
			options: needsAttentionOptions,
			selected: st.filters.needsAttention,
		},
		{
			key: 'essayStatus',
			title: 'Essay Status',
			options: essayStatusOptions,
			selected: st.filters.essayStatus,
		},
	]

	const lockedCount = st.lockedColumnIds?.length || 0

	const managerColumns = (st.activeColumns || []).filter(
		c => !st.isLockedColumn(c.id),
	)

	const onChangeKey = useCallback(
		(key, nextSelected) => {
			st.setFilter(key, nextSelected)
		},
		[st],
	)

	const onApply = useCallback(() => {
		const apps = applicationsStore.applications
		const rowList = Array.isArray(apps) ? apps : []
		const aggregateMap = essaySummaryStore.aggregateByTargetId
		const essaysMap = essaySummaryStore.essaysByTargetId

		const allowed = {
			country: new Set(buildCountryFilterOptions(rowList).map(option => option.value)),
			needsAttention: new Set(
				buildNeedsAttentionFilterOptions(rowList, aggregateMap).map(option => option.value),
			),
			essayStatus: new Set(
				buildEssayStatusFilterOptions(rowList, aggregateMap, essaysMap).map(
					option => option.value,
				),
			),
		}

		const draft = {
			country: st.filters.country || [],
			needsAttention: st.filters.needsAttention || [],
			essayStatus: st.filters.essayStatus || [],
		}

		const sanitized = sanitizePanelFilters(draft, allowed)
		const uniqueEssayStatus = Array.from(new Set(sanitized.essayStatus))

		const next = {
			country: sanitized.country,
			needsAttention: sanitized.needsAttention,
			essayStatus: uniqueEssayStatus,
		}

		st.setFilter('country', next.country)
		st.setFilter('needsAttention', next.needsAttention)
		st.setFilter('essayStatus', next.essayStatus)
		applicationsStore.applyPanelFilters(next)
		onClose?.()
	}, [st, onClose])

	const onActivateColumn = useCallback(
		id => {
			st.moveToActive(id)
		},
		[st],
	)

	const onDeactivateColumn = useCallback(
		id => {
			st.moveToInactive(id)
		},
		[st],
	)

	const onReorderActive = useCallback(
		(from, to) => {
			st.reorderActive(from + lockedCount, to + lockedCount)
		},
		[st, lockedCount],
	)

	return {
		filtersConfig,
		onChangeKey,
		onApply,

		inactiveColumns: st.inactiveColumns,
		managerColumns,

		onActivateColumn,
		onDeactivateColumn,
		onReorderActive,
	}
}
